import React from 'react';
import { GraduationCap, Users, Award, BookOpen, Target, Star, Shield, Sparkles, Heart } from 'lucide-react';

const OQueEAlumni = () => {
    const alumniCategories = [
        {
            icon: <Star className="w-5 h-5" />,
            title: "Membros Fundadores",
            description: "Pioneiros que contribuíram para o estabelecimento e crescimento da comunidade Alumni-UEM."
        },
        {
            icon: <Users className="w-5 h-5" />,
            title: "Membros Efectivos",
            description: "Graduados e egressos que participam ativamente das atividades e iniciativas da comunidade."
        },
        {
            icon: <Award className="w-5 h-5" />,
            title: "Membros Honorários",
            description: "Personalidades e colaboradores que prestaram serviços relevantes à UEM e à comunidade."
        }
    ];

    const professionalCourses = [
        "Curso de formação de professores",
        "Faculdade de Combatentes e Trabalhadores de Vanguarda (FACOTRAV)",
        "Propedêutico de Letras e Propedêutico de Ciências",
        "Cursos médios intensivos de Gestão de Empresas",
        "Curso de aprimoramento do Português"
    ];

    return (
        <div className="max-w-6xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-12">
            {/* Header Section */}
            <div className="mb-12 bg-emerald-50 rounded-xl p-6 border border-emerald-100/50">
                <div className="border-l-4  border-emerald-500 pl-4 mb-4">
                    <h1 className="text-3xl font-bold text-gray-800 tracking-tight">O que é Alumni?</h1>
                    <p className="text-gray-500 text-base font-light mt-1">
                        Conheça a comunidade de graduados da Universidade Eduardo Mondlane
                    </p>
                </div>
            </div>

            {/* Hero Section */}
            <div className="bg-gradient-to-r from-emerald-50/50 to-gray-50/50 rounded-xl p-8 mb-10 border border-emerald-100/30">
                <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                        <GraduationCap className="w-5 h-5 text-emerald-600" />
                    </div>
                    <h2 className="text-xl font-light text-gray-700">Definição e Origem</h2>
                </div>
                <p className="text-gray-600 leading-relaxed mb-3">
                    Etimologicamente, <span className="font-medium text-emerald-600">Alumni</span>, plural de Alumnus, é o nome usado 
                    para designar alunos graduados ou antigos alunos de uma universidade. 
                    Para a UEM, o termo Alumni designa todos os <span className="font-medium">graduados e egressos</span> da 
                    Universidade Eduardo Mondlane.
                </p>
                <p className="text-gray-600 leading-relaxed">
                    Designa-se graduado da UEM o estudante que obteve um grau académico 
                    (Bacharelato, Licenciatura e/ou Doutoramento), bem como os estudantes que concluíram 
                    cursos profissionalizantes oferecidos pela instituição.
                </p>
            </div>

            {/* Professional Courses Section */}
            <div className="bg-white rounded-xl border border-gray-100 p-6 mb-10">
                <div className="flex items-center gap-2 mb-4">
                    <BookOpen className="w-4 h-4 text-emerald-500" />
                    <h2 className="text-sm font-medium text-gray-700">Cursos Profissionalizantes</h2>
                </div>
                <p className="text-gray-500 text-sm mb-4">
                    Fazem parte da comunidade Alumni, os estudantes que concluíram cursos profissionalizantes oferecidos pela UEM:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {professionalCourses.map((course, index) => (
                        <div key={index} className="flex items-center gap-2">
                            <div className="w-1.5 h-1.5 rounded-full bg-emerald-400"></div>
                            <span className="text-gray-600 text-sm">{course}</span>
                        </div>
                    ))}
                </div>
            </div>

            {/* Main Definition Section */}
            <div className="bg-gradient-to-r from-emerald-50/30 to-gray-50/30 rounded-xl p-8 mb-10 border border-emerald-100/30">
                <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                        <Target className="w-5 h-5 text-emerald-600" />
                    </div>
                    <h2 className="text-xl font-light text-gray-700">Comunidade Alumni-UEM</h2>
                </div>
                <p className="text-gray-600 leading-relaxed mb-3">
                    A UEM define os Alumni como uma <span className="font-medium text-emerald-600">Comunidade de Graduados</span>, 
                    ou seja, Comunidade Alumni-UEM.
                </p>
                <p className="text-gray-600 leading-relaxed">
                    Os Alumni são os mais importantes embaixadores da universidade e são os principais 
                    responsáveis pela imagem da mesma. A Iniciativa Alumni da UEM foi oficialmente 
                    lançada a <span className="font-medium">22 de Agosto de 2014</span>, pelo Magnífico Reitor da UEM, 
                    Prof. Doutor Orlando António Quilambo, no Centro Cultural Universitário da UEM.
                </p>
            </div>

            {/* Categories Section */}
            <div className="mb-10">
                <div className="flex items-center gap-2 mb-6">
                    <Users className="w-5 h-5 text-emerald-500" />
                    <h2 className="text-lg font-medium text-gray-700">Categorias de Membros</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    {alumniCategories.map((category, index) => (
                        <div 
                            key={index}
                            className="bg-white rounded-xl border border-gray-100 p-6 hover:shadow-md transition-all duration-300 text-center group"
                        >
                            <div className="flex justify-center mb-3">
                                <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center group-hover:bg-emerald-100 transition-colors">
                                    <div className="text-emerald-500">
                                        {category.icon}
                                    </div>
                                </div>
                            </div>
                            <h3 className="text-base font-medium text-gray-700 mb-2">
                                {category.title}
                            </h3>
                            <p className="text-gray-500 text-xs leading-relaxed">
                                {category.description}
                            </p>
                        </div>
                    ))}
                </div>
            </div>

            {/* Ambassador Section */}
            <div className="bg-gray-50/50 rounded-xl p-6 mb-10 border border-gray-100">
                <div className="flex gap-4">
                    <div className="flex-shrink-0">
                        <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                            <Shield className="w-5 h-5 text-emerald-600" />
                        </div>
                    </div>
                    <div>
                        <h2 className="text-base font-medium text-gray-700 mb-2">
                            Embaixador da Comunidade Alumni-UEM
                        </h2>
                        <p className="text-gray-500 text-sm leading-relaxed">
                            O embaixador Alumni-UEM representa a UEM no país e no mundo, 
                            através da defesa da visão e missão da instituição e da protecção dos 
                            seus símbolos e insígnias, que fazem da UEM uma marca de
                            vanguarda no ensino superior no país, na região e no mundo.
                        </p>
                    </div>
                </div>
            </div>

            {/* Impact Stats Section */}
            <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 rounded-xl p-6 mb-10">
                <div className="flex justify-center mb-4">
                    <Sparkles className="w-6 h-6 text-white/80" />
                </div>
                <h3 className="text-lg font-light text-white text-center mb-6">
                    Nossa Comunidade em Números
                </h3>
                <div className="flex flex-row justify-around items-center">
                    <div className="text-center">
                        <div className="text-2xl font-light text-white mb-1">+10.000</div>
                        <div className="text-xs text-emerald-100">Graduados</div>
                    </div>
                    <div className="w-px h-8 bg-emerald-500/50"></div>
                    <div className="text-center">
                        <div className="text-2xl font-light text-white mb-1">50+</div>
                        <div className="text-xs text-emerald-100">Anos de História</div>
                    </div>
                    <div className="w-px h-8 bg-emerald-500/50"></div>
                    <div className="text-center">
                        <div className="text-2xl font-light text-white mb-1">3</div>
                        <div className="text-xs text-emerald-100">Categorias</div>
                    </div>
                    <div className="w-px h-8 bg-emerald-500/50"></div>
                    <div className="text-center">
                        <div className="text-2xl font-light text-white mb-1">2014</div>
                        <div className="text-xs text-emerald-100">Lançamento</div>
                    </div>
                </div>
            </div>

            {/* Call to Action */}
            <div className="text-center">
                <button 
                    onClick={() => window.location.href = "/embaixadores"}
                    className="bg-emerald-600 text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors inline-flex items-center gap-2 shadow-sm"
                >
                    <Heart size={14} />
                    Conheça Nossos Embaixadores
                </button>
            </div>
        </div>
    );
};

export default OQueEAlumni;